// MARK://dhk8/pos/pages/BarraDisplayPage
// Pantalla DE CLIENTE para el POS de Barra de 2 pantallas — full-screen, sin
// chrome operativo. La segunda ventana del navegador del cajero la abre en el
// segundo monitor; el cliente VE su cuenta crecer en vivo mientras el cajero la
// captura en la ventana primaria (/pos/barra → FastBarPage).
//
// Mecanismo de sincronización: BroadcastChannel del MISMO navegador. La
// FastBarPage broadcastea un snapshot del carrito en cada cambio; aquí lo
// recibimos vía `subscribeCart` y re-renderizamos.
//
// Reglas de diseño NintaiGO (estrictas):
//   - PROHIBIDO `border-radius` (excepto dots de estado).
//   - PROHIBIDO `box-shadow`. Elevación con borde 1px o inversión bg/fg.
//   - Esquinas RECTAS, números/totales grandes para legibilidad a distancia.
//   - SIN nav lateral, SIN botones operativos. Sólo marca, productos, totales.
//
// Ruta pública (FUERA del Shell, sin login). El operador la abre con
// `?tenant=<slug>` opcionalmente — si se pasa se usa para `getTenantBySlug` y
// pintar branding antes del primer mensaje del operador.

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import BrandMark from '../components/BrandMark.js'
import { formatCents } from '../lib/money.js'
import {
  subscribeCart,
  type CartSnapshot,
} from '../hooks/use-cart-broadcast.js'
import { useTenantId } from '../hooks/useTenantId.js'
import { getBranding } from '../lib/branding-loader.js'

// Snapshot vacío inicial — la pantalla nunca queda "blanca", arranca con un
// estado de espera bonito y limpio.
const EMPTY_SNAPSHOT: CartSnapshot = {
  v: 1,
  ts: 0,
  lines: [],
  subtotalCents: 0,
  promos: [],
  totalCents: 0,
  lastSale: null,
}

export default function BarraDisplayPage() {
  const [searchParams] = useSearchParams()
  // El tenant slug es opcional; sirve sólo para fallback de marca si el
  // operador todavía no broadcasteó (el endpoint getTenantBySlug ya carga
  // logo/nombre en `branding-loader.ts`). Como POS y display corren en la
  // misma máquina (mismo origen), la `loadBranding()` que dispara main.tsx ya
  // pinta la marca del tenant logueado. El `?tenant=` se respeta como hint.
  const tenantHint = searchParams.get('tenant') ?? null

  const [snap, setSnap] = useState<CartSnapshot>(EMPTY_SNAPSHOT)
  const [now, setNow] = useState(() => Date.now())
  const [staleSeconds, setStaleSeconds] = useState(0)
  const [recentlyClosed, setRecentlyClosed] = useState<{ folio: string; totalCents: number } | null>(null)

  // S1-5 hardening 2026-05-24: el canal se scopa por tenantId activo
  // (mismo en POS y display porque corren mismo origen + same auth-store).
  const tenantId = useTenantId()
  // Suscripción al BroadcastChannel del operador. Reabrir si cambia tenantId.
  useEffect(() => {
    const unsub = subscribeCart(
      (payload) => {
        setSnap(payload)
        // Si llegó un cierre de venta, lo mostramos por 6s y luego limpiamos.
        if (payload.lastSale) {
          setRecentlyClosed(payload.lastSale)
          const id = setTimeout(() => setRecentlyClosed(null), 6_000)
          return () => clearTimeout(id)
        }
        return undefined
      },
      () => {
        // El operador cerró la ventana — quedamos en estado de espera limpio.
        setSnap(EMPTY_SNAPSHOT)
      },
      tenantId,
    )
    return unsub
  }, [tenantId])

  // Reloj + indicador de "stale" (sin updates en >30s).
  useEffect(() => {
    const id = setInterval(() => {
      const t = Date.now()
      setNow(t)
      setStaleSeconds(snap.ts > 0 ? Math.floor((t - snap.ts) / 1000) : 0)
    }, 1_000)
    return () => clearInterval(id)
  }, [snap.ts])

  // Marca: prioridad → snapshot del operador > branding cargado en main.tsx > default.
  const brandName = snap.branding?.name ?? getBranding().name ?? 'NintaiGO'
  const logoUrl = snap.branding?.logoUrl ?? getBranding().logoUrl ?? null

  const totalDiscounts = useMemo(
    () => snap.promos.reduce((s, p) => s + p.amountCents, 0),
    [snap.promos],
  )

  const empty = snap.lines.length === 0 && !recentlyClosed
  const timeLabel = new Date(now).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })

  // `tenantHint` se conserva sólo como debug discreto en el header.
  return (
    <div className="bd-root">
      {/* Header — marca + reloj. SIN nav, SIN botones. */}
      <header className="bd-header">
        <div className="bd-brand">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="bd-brand-logo" />
          ) : (
            <BrandMark size="lg" />
          )}
          <div className="bd-brand-text">
            <div className="bd-eyebrow">Punto de venta · Barra</div>
            <h1 className="bd-brand-name">{brandName}</h1>
          </div>
        </div>
        <div className="bd-clock">
          <time>{timeLabel}</time>
          {tenantHint && <div className="bd-tenant-hint">tenant · {tenantHint}</div>}
        </div>
      </header>

      <main className="bd-main">
        {/* Lista de productos del carrito (live). */}
        <section className="bd-cart">
          <header className="bd-cart-head">
            <h2 className="bd-section-title">Tu cuenta</h2>
            <span className="bd-cart-count">
              {snap.lines.reduce((s, l) => s + l.quantity, 0)} ítem
              {snap.lines.reduce((s, l) => s + l.quantity, 0) === 1 ? '' : 's'}
            </span>
          </header>

          {empty && (
            <div className="bd-empty">
              <p className="bd-empty-title">Bienvenido</p>
              <p className="bd-empty-sub">Tu cuenta aparecerá aquí cuando el cajero comience a agregar productos.</p>
            </div>
          )}

          {!empty && recentlyClosed && (
            <div className="bd-thanks">
              <p className="bd-thanks-eyebrow">¡Gracias por tu compra!</p>
              <p className="bd-thanks-folio">Folio {recentlyClosed.folio}</p>
              <p className="bd-thanks-total">{formatCents(recentlyClosed.totalCents)}</p>
            </div>
          )}

          {!recentlyClosed && snap.lines.length > 0 && (
            <ul className="bd-lines">
              {snap.lines.map((l) => {
                // 2026-05-25 fix: el snapshot envía `unitPriceCents` ya
                // sumado con los add-ons; para mostrar al cliente el
                // precio BASE (igual que en la barra del operador, ver
                // FastBarPage Lane C) derivamos baseUnitCents quitando
                // la suma de deltas. Cero modifiers ⇒ base == unit.
                const deltaSum = l.modifiers.reduce((a, m) => a + m.priceDeltaCents, 0)
                const baseUnitCents = l.unitPriceCents - deltaSum
                return (
                  <li key={l.lineId} className="bd-line">
                    <div className="bd-line-qty">×{l.quantity}</div>
                    <div className="bd-line-body">
                      <div className="bd-line-name">{l.name}</div>
                      {l.modifiers.length > 0 && (
                        <ul className="bd-line-mods">
                          {l.modifiers.map((m, i) => (
                            <li key={`${l.lineId}-m-${i}`} className="bd-line-mod">
                              <span className="bd-line-mod-name">{m.name}</span>
                              {m.priceDeltaCents !== 0 && (
                                <span className="bd-line-mod-delta">
                                  {m.priceDeltaCents > 0 ? '+' : ''}
                                  {formatCents(m.priceDeltaCents)}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {l.notes && <div className="bd-line-notes">{`“${l.notes}”`}</div>}
                    </div>
                    <div className="bd-line-amount">
                      <div className="bd-line-unit">{formatCents(baseUnitCents)} c/u</div>
                      <div className="bd-line-total">{formatCents(l.unitPriceCents * l.quantity)}</div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {/* Barra inferior — subtotal / promos / total. */}
        <footer className="bd-totals">
          <div className="bd-totals-row">
            <span className="bd-totals-label">Subtotal</span>
            <span className="bd-totals-value">{formatCents(snap.subtotalCents)}</span>
          </div>

          {snap.promos.length > 0 && (
            <div className="bd-promos">
              <div className="bd-promos-title">Promociones aplicadas</div>
              <ul className="bd-promos-list">
                {snap.promos.map((p, i) => (
                  <li key={`promo-${i}`} className="bd-promos-item">
                    <span className="bd-promos-dot" aria-hidden />
                    <span className="bd-promos-label">{p.label}</span>
                    <span className="bd-promos-amount">−{formatCents(p.amountCents)}</span>
                  </li>
                ))}
              </ul>
              {totalDiscounts > 0 && (
                <div className="bd-promos-total">
                  <span>Total de descuentos</span>
                  <span>−{formatCents(totalDiscounts)}</span>
                </div>
              )}
            </div>
          )}

          <div className="bd-totals-row bd-totals-row--main">
            <span className="bd-totals-label-main">Total</span>
            <span className="bd-totals-value-main">{formatCents(snap.totalCents)}</span>
          </div>
        </footer>
      </main>

      {/* Footer de estado: discretísimo, sólo para el cajero saber si está
          recibiendo. NO es UI para el cliente. */}
      <div className="bd-status">
        <span className={`bd-status-dot bd-status-dot--${snap.ts === 0 ? 'idle' : staleSeconds > 30 ? 'stale' : 'live'}`} />
        <span className="bd-status-text">
          {snap.ts === 0 ? 'Esperando al cajero' : staleSeconds > 30 ? `Sin cambios hace ${staleSeconds}s` : 'En vivo'}
        </span>
      </div>

      <style>{styles}</style>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────
// Diseño limpio NintaiGO: superficies oscuras (Restaurant Dark), tipografía
// grande, esquinas rectas (RADIUS = 0), elevación por borde 1px o inversión.
// SIN box-shadow, SIN border-radius (excepto dots de estado).
const styles = `
  .bd-root {
    position: fixed; inset: 0;
    display: flex; flex-direction: column;
    background: var(--pos-canvas, #0d1f17);
    color: var(--pos-fg, #f4f7f5);
    font-family: var(--font-sans, system-ui);
    overflow: hidden;
  }

  /* Header: marca + reloj */
  .bd-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 2rem 2.5rem 1.5rem;
    border-bottom: 1px solid var(--pos-separator, rgba(255,255,255,0.08));
  }
  .bd-brand { display: flex; align-items: center; gap: 1.25rem; }
  .bd-brand-logo {
    width: 64px; height: 64px; object-fit: contain;
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.18));
    background: var(--pos-elevated, rgba(255,255,255,0.04));
  }
  .bd-brand-text { display: flex; flex-direction: column; gap: 0.25rem; }
  .bd-eyebrow {
    font-family: var(--font-mono, ui-monospace);
    font-size: 0.75rem; letter-spacing: 0.16em; text-transform: uppercase;
    color: var(--pos-fg-tertiary, #8fa698);
  }
  .bd-brand-name {
    margin: 0; font-size: 2rem; font-weight: 700; letter-spacing: -0.01em;
    color: var(--pos-fg, #f4f7f5);
  }
  .bd-clock { text-align: right; display: flex; flex-direction: column; gap: 0.25rem; align-items: flex-end; }
  .bd-clock time {
    font-family: var(--font-mono, ui-monospace);
    font-size: 2.5rem; font-weight: 600; font-variant-numeric: tabular-nums;
    color: var(--pos-fg-secondary, #cdd9d2);
  }
  .bd-tenant-hint {
    font-family: var(--font-mono, ui-monospace);
    font-size: 0.7rem; letter-spacing: 0.06em; color: var(--pos-fg-tertiary, #8fa698);
    opacity: 0.6;
  }

  /* Main: carrito + barra inferior */
  .bd-main {
    flex: 1; display: grid; grid-template-rows: 1fr auto;
    min-height: 0;
  }

  /* Sección del carrito */
  .bd-cart {
    display: flex; flex-direction: column; min-height: 0;
    padding: 2rem 2.5rem;
  }
  .bd-cart-head {
    display: flex; justify-content: space-between; align-items: baseline;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--pos-separator, rgba(255,255,255,0.08));
    margin-bottom: 1rem;
  }
  .bd-section-title {
    margin: 0; font-size: 1.5rem; font-weight: 600; letter-spacing: -0.005em;
    color: var(--pos-fg, #f4f7f5);
  }
  .bd-cart-count {
    font-family: var(--font-mono, ui-monospace);
    font-size: 0.85rem; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--pos-fg-tertiary, #8fa698);
  }
  .bd-empty {
    flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 2rem;
  }
  .bd-empty-title {
    margin: 0 0 0.5rem; font-size: 3rem; font-weight: 600; letter-spacing: -0.02em;
    color: var(--pos-fg, #f4f7f5);
  }
  .bd-empty-sub {
    margin: 0; font-size: 1.25rem; color: var(--pos-fg-tertiary, #8fa698);
    max-width: 36rem;
  }

  /* Cierre de venta — overlay efímero (6s) */
  .bd-thanks {
    flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 2rem;
    border: 1px solid var(--pos-tint, #1f9d57);
    background: var(--pos-tint-soft, rgba(31,157,87,0.12));
  }
  .bd-thanks-eyebrow {
    margin: 0 0 1rem; font-family: var(--font-mono, ui-monospace);
    font-size: 1rem; letter-spacing: 0.16em; text-transform: uppercase;
    color: var(--pos-tint, #1f9d57);
  }
  .bd-thanks-folio {
    margin: 0; font-size: 1.5rem; color: var(--pos-fg-secondary, #cdd9d2);
    font-family: var(--font-mono, ui-monospace);
  }
  .bd-thanks-total {
    margin: 1rem 0 0; font-size: 5rem; font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--pos-fg, #f4f7f5);
  }

  /* Lista de líneas */
  .bd-lines {
    list-style: none; margin: 0; padding: 0;
    flex: 1; overflow-y: auto;
    display: flex; flex-direction: column;
  }
  .bd-line {
    display: grid; grid-template-columns: 4rem 1fr auto;
    gap: 1.5rem; align-items: baseline;
    padding: 1.25rem 0;
    border-bottom: 1px solid var(--pos-separator, rgba(255,255,255,0.08));
  }
  .bd-line:last-child { border-bottom: none; }
  .bd-line-qty {
    font-family: var(--font-mono, ui-monospace);
    font-size: 2rem; font-weight: 600; line-height: 1;
    color: var(--pos-tint, #1f9d57);
    font-variant-numeric: tabular-nums;
  }
  .bd-line-body { display: flex; flex-direction: column; gap: 0.4rem; min-width: 0; }
  .bd-line-name {
    font-size: 1.5rem; font-weight: 500; line-height: 1.2;
    color: var(--pos-fg, #f4f7f5);
  }
  .bd-line-mods {
    list-style: none; margin: 0; padding: 0;
    display: flex; flex-direction: column; gap: 0.15rem;
  }
  .bd-line-mod {
    display: flex; justify-content: space-between; gap: 1rem;
    font-size: 1rem; color: var(--pos-fg-secondary, #cdd9d2);
  }
  .bd-line-mod-name { font-family: var(--font-mono, ui-monospace); font-size: 0.95rem; }
  .bd-line-mod-delta {
    font-family: var(--font-mono, ui-monospace); font-size: 0.95rem;
    font-variant-numeric: tabular-nums; color: var(--pos-fg-tertiary, #8fa698);
  }
  .bd-line-notes {
    font-family: var(--font-mono, ui-monospace);
    font-size: 0.95rem; font-style: italic;
    color: var(--pos-fg-tertiary, #8fa698);
  }
  .bd-line-amount {
    display: flex; flex-direction: column; gap: 0.25rem; align-items: flex-end;
    font-variant-numeric: tabular-nums;
  }
  .bd-line-unit {
    font-family: var(--font-mono, ui-monospace);
    font-size: 0.95rem; color: var(--pos-fg-tertiary, #8fa698);
  }
  .bd-line-total {
    font-size: 1.75rem; font-weight: 600;
    color: var(--pos-fg, #f4f7f5);
  }

  /* Barra de totales (sticky bottom) */
  .bd-totals {
    border-top: 1px solid var(--pos-separator-strong, rgba(255,255,255,0.18));
    background: var(--pos-elevated, rgba(255,255,255,0.03));
    padding: 1.5rem 2.5rem 2rem;
    display: flex; flex-direction: column; gap: 1rem;
  }
  .bd-totals-row {
    display: flex; justify-content: space-between; align-items: baseline;
    font-size: 1.5rem; color: var(--pos-fg-secondary, #cdd9d2);
    font-variant-numeric: tabular-nums;
  }
  .bd-totals-row--main {
    padding-top: 1rem;
    border-top: 1px solid var(--pos-separator, rgba(255,255,255,0.08));
  }
  .bd-totals-label { font-size: 1.5rem; }
  .bd-totals-value { font-family: var(--font-mono, ui-monospace); font-size: 1.5rem; }
  .bd-totals-label-main {
    font-size: 2.5rem; font-weight: 600; color: var(--pos-fg, #f4f7f5);
    letter-spacing: -0.01em;
  }
  .bd-totals-value-main {
    font-family: var(--font-mono, ui-monospace);
    font-size: 4rem; font-weight: 700;
    color: var(--pos-fg, #f4f7f5);
    letter-spacing: -0.02em;
  }

  /* Promociones */
  .bd-promos {
    display: flex; flex-direction: column; gap: 0.5rem;
    padding: 1rem 1.25rem;
    border: 1px solid var(--pos-tint, #1f9d57);
    background: var(--pos-tint-soft, rgba(31,157,87,0.10));
  }
  .bd-promos-title {
    font-family: var(--font-mono, ui-monospace);
    font-size: 0.8rem; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--pos-tint, #1f9d57);
  }
  .bd-promos-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.4rem; }
  .bd-promos-item {
    display: grid; grid-template-columns: auto 1fr auto; gap: 0.75rem; align-items: center;
    font-size: 1.1rem; color: var(--pos-fg-secondary, #cdd9d2);
  }
  /* Dots de estado / promo — única excepción permitida al border-radius. */
  .bd-promos-dot {
    width: 8px; height: 8px;
    border-radius: var(--radius-dot);
    background: var(--pos-tint, #1f9d57);
  }
  .bd-promos-label {}
  .bd-promos-amount {
    font-family: var(--font-mono, ui-monospace);
    font-variant-numeric: tabular-nums;
    color: var(--pos-tint, #1f9d57);
  }
  .bd-promos-total {
    display: flex; justify-content: space-between;
    padding-top: 0.5rem; margin-top: 0.25rem;
    border-top: 1px solid var(--pos-separator, rgba(255,255,255,0.10));
    font-size: 1.1rem; color: var(--pos-tint, #1f9d57);
    font-variant-numeric: tabular-nums;
  }

  /* Status footer — discreto, esquina superior-izquierda del header (info para el cajero) */
  .bd-status {
    position: absolute; top: 0.5rem; left: 0.5rem;
    display: flex; align-items: center; gap: 0.4rem;
    padding: 0.2rem 0.55rem;
    border: 1px solid var(--pos-separator, rgba(255,255,255,0.08));
    background: var(--pos-elevated, rgba(255,255,255,0.04));
    font-family: var(--font-mono, ui-monospace);
    font-size: 0.65rem; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--pos-fg-tertiary, #8fa698);
    opacity: 0.4;
    pointer-events: none;
  }
  .bd-status-dot { width: 8px; height: 8px; border-radius: var(--radius-dot); }
  .bd-status-dot--live  { background: var(--pos-tint, #1f9d57); }
  .bd-status-dot--stale { background: var(--pos-warning, #d4a017); }
  .bd-status-dot--idle  { background: var(--pos-fg-tertiary, #8fa698); }
`
